/**
 * How a pattern is stored in CouchDB.
 *
 * Each user has two databases:
 *   user_<userId>_patterns  one document per pattern: everything except the pieces, plus the
 *                           thumbnail and dates. The home page lists patterns from this alone.
 *   user_<userId>_pieces    one document per piece, so editing a point re-sends one small piece
 *                           instead of the whole pattern.
 *
 * Piece documents are keyed "<patternId>:<pieceId>", so all pieces of a pattern sort together
 * and can be read with one key range. They also carry `pattern_id` for replication filters.
 */

export const patternsDbName = (userId) => `user_${userId}_patterns`;
export const piecesDbName = (userId) => `user_${userId}_pieces`;
export const pieceDocId = (patternId, pieceId) => `${patternId}:${pieceId}`;

/** Key range that selects every piece document of one pattern in an allDocs query. */
export const pieceKeyRange = (patternId) => ({
  startkey: `${patternId}:`,
  endkey: `${patternId}:￰`,
});

export const PATTERN_DOC_TYPE = 'pattern';

/** Deletion markers for every piece of a pattern, keeping pattern_id so filtered replication carries them. */
export const pieceDeletions = (patternId, rows) => rows.map((row) => ({
  _id: row.id || row._id,
  _rev: (row.value && row.value.rev) || row._rev,
  _deleted: true,
  type: 'piece',
  pattern_id: patternId,
}));
export const PIECE_DOC_TYPE = 'piece';

const withoutCouchFields = (doc) => {
  if (!doc) return doc;
  const { _id, _rev, _revisions, _conflicts, ...rest } = doc;
  return rest;
};

export default class PatternStoreHelper {
  /**
   * Splits a whole pattern into the body of its pattern document and its piece documents.
   * `extra` is merged into the pattern document, for example the thumbnail or dates.
   */
  static split(patternId, pattern, extra = {}) {
    const { pieces = {}, ...rest } = pattern || {};
    const pieceIds = Object.keys(pieces);
    const patternDoc = {
      _id: patternId,
      type: PATTERN_DOC_TYPE,
      pattern: rest,
      piece_ids: pieceIds,
      ...extra,
    };
    const pieceDocs = pieceIds.map((pieceId) => ({
      _id: pieceDocId(patternId, pieceId),
      type: PIECE_DOC_TYPE,
      pattern_id: patternId,
      piece_id: pieceId,
      piece: pieces[pieceId],
    }));
    return { patternDoc, pieceDocs };
  }

  /**
   * Rebuilds the whole pattern from its pattern document and piece documents.
   * Pieces follow the order in `piece_ids`; piece documents that are not listed are ignored,
   * which covers a piece that was deleted while the deletion is still syncing.
   */
  static join(patternDoc, pieceDocs = []) {
    if (!patternDoc) return null;
    const byId = {};
    pieceDocs.forEach((doc) => {
      if (doc && !doc._deleted && doc.piece_id) byId[doc.piece_id] = doc.piece;
    });
    const order = patternDoc.piece_ids || Object.keys(byId);
    const pieces = {};
    order.forEach((pieceId) => {
      if (byId[pieceId]) pieces[pieceId] = byId[pieceId];
    });
    return { ...(patternDoc.pattern || {}), pieces };
  }

  /**
   * Piece ids the pattern document lists but that have no piece document yet.
   * Replication can deliver the pattern document before its pieces; a pattern with missing pieces
   * must not be loaded into the editor, because saving it would delete those pieces.
   */
  static missingPieces(patternDoc, pieceDocs = []) {
    if (!patternDoc) return [];
    const present = new Set(pieceDocs.filter((doc) => doc && !doc._deleted).map((doc) => doc.piece_id));
    return (patternDoc.piece_ids || []).filter((pieceId) => !present.has(pieceId));
  }

  /**
   * Works out which documents a save has to write.
   * Compares the new pattern with the stored documents and returns only what changed:
   *   patternDoc  the new pattern document, or null if it is unchanged
   *   upserts     piece documents that are new or changed
   *   deletes     piece documents for pieces that no longer exist
   * `storedPatternDoc` and `storedPieceDocs` may include _id and _rev; they are carried over.
   */
  static diff(patternId, pattern, storedPatternDoc, storedPieceDocs = []) {
    const { patternDoc, pieceDocs } = PatternStoreHelper.split(patternId, pattern);
    const storedById = {};
    storedPieceDocs.forEach((doc) => { storedById[doc._id] = doc; });

    const upserts = [];
    pieceDocs.forEach((doc) => {
      const stored = storedById[doc._id];
      if (!stored) {
        upserts.push(doc);
      } else if (!PatternStoreHelper.same(stored.piece, doc.piece)) {
        upserts.push({ ...doc, _rev: stored._rev });
      }
    });

    const keep = new Set(pieceDocs.map((doc) => doc._id));
    // Deletion markers keep pattern_id, so replication filtered to one pattern still carries them.
    const deletes = storedPieceDocs
      .filter((doc) => !keep.has(doc._id))
      .map((doc) => ({
        _id: doc._id, _rev: doc._rev, _deleted: true, type: PIECE_DOC_TYPE, pattern_id: patternId, piece_id: doc.piece_id,
      }));

    const storedBody = storedPatternDoc
      ? { pattern: storedPatternDoc.pattern, piece_ids: storedPatternDoc.piece_ids }
      : null;
    const patternChanged = !storedBody
      || !PatternStoreHelper.same(storedBody, { pattern: patternDoc.pattern, piece_ids: patternDoc.piece_ids });
    const piecesChanged = upserts.length > 0 || deletes.length > 0;

    return {
      patternChanged,
      piecesChanged,
      patternDoc: patternChanged || piecesChanged
        ? { ...withoutCouchFields(storedPatternDoc), ...patternDoc, ...(storedPatternDoc ? { _rev: storedPatternDoc._rev } : {}) }
        : null,
      upserts,
      deletes,
    };
  }

  /** Deep equality for plain JSON data, which is all a pattern contains. */
  static same(a, b) {
    return JSON.stringify(PatternStoreHelper.sortKeys(a)) === JSON.stringify(PatternStoreHelper.sortKeys(b));
  }

  static sortKeys(value) {
    if (Array.isArray(value)) return value.map(PatternStoreHelper.sortKeys);
    if (value && typeof value === 'object') {
      const sorted = {};
      Object.keys(value).sort().forEach((key) => {
        if (value[key] !== undefined) sorted[key] = PatternStoreHelper.sortKeys(value[key]);
      });
      return sorted;
    }
    return value;
  }
}
